import Text from '@cartwhl/ui/base/Text';

type LabelPosition = 'above' | 'below' | 'left' | 'right';

interface ParetoPoint {
  label: string;
  x: number;
  y: number;
  series?: string;
  labelPosition?: LabelPosition;
}

interface ParetoChartProps {
  xLabel: string;
  yLabel: string;
  // The first and last ticks set each axis's domain.
  xTicks: number[];
  yTicks: number[];
  xTickLabels?: string[];
  xScale?: 'linear' | 'log';
  // Points in this series are highlighted and joined in the order given.
  frontier: string;
  points: ParetoPoint[];
  caption?: string;
}

const labelPositionClassNames: Record<LabelPosition, string> = {
  above: '-translate-x-1/2 -translate-y-[calc(100%+10px)]',
  below: '-translate-x-1/2 translate-y-[10px]',
  left: '-translate-x-[calc(100%+12px)] -translate-y-1/2',
  right: 'translate-x-[12px] -translate-y-1/2',
};

function scale(value: number, ticks: number[], log: boolean) {
  const transform = log ? Math.log10 : (n: number) => n;
  const min = transform(ticks[0]);
  const max = transform(ticks[ticks.length - 1]);
  return ((transform(value) - min) / (max - min)) * 100;
}

export default function ParetoChart({
  xLabel,
  yLabel,
  xTicks,
  yTicks,
  xTickLabels,
  xScale = 'linear',
  frontier,
  points,
  caption,
}: ParetoChartProps) {
  const log = xScale === 'log';
  const position = (point: ParetoPoint) => ({
    left: scale(point.x, xTicks, log),
    top: 100 - scale(point.y, yTicks, false),
  });
  const frontierPoints = points.filter((point) => point.series === frontier);
  const otherPoints = points.filter((point) => point.series !== frontier);

  return (
    <figure className="article-breakout my-8 px-4 lg:my-12 lg:px-0">
      <div className="bg-secondary rounded-2xl px-6 pb-12 pt-6 lg:px-10 lg:pb-14 lg:pt-10">
        <div className="mb-6 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2">
          <Text as="span" variant="web/body/medium/md" className="text-primary">
            {yLabel}
          </Text>
          <span className="flex items-center gap-2">
            <span className="size-2.5 rounded-full bg-green-50" />
            <Text
              as="span"
              variant="web/body/medium/xs"
              className="text-secondary"
            >
              {frontier}
            </Text>
          </span>
        </div>
        <div className="ml-10 aspect-square sm:aspect-[4/3] lg:aspect-[2/1]">
          <div
            className="relative h-full w-full"
            role="img"
            aria-label={`${yLabel} versus ${xLabel}. ${points
              .map((point) => `${point.label}: ${point.y} at ${point.x}`)
              .join('; ')}.`}
          >
            <svg
              className="absolute inset-0 h-full w-full overflow-visible"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              {yTicks.map((tick) => {
                const y = 100 - scale(tick, yTicks, false);
                return (
                  <line
                    key={tick}
                    x1="0"
                    x2="100"
                    y1={y}
                    y2={y}
                    stroke="var(--color-gray-30)"
                    vectorEffect="non-scaling-stroke"
                  />
                );
              })}
              <polyline
                points={frontierPoints
                  .map((point) => {
                    const { left, top } = position(point);
                    return `${left},${top}`;
                  })
                  .join(' ')}
                fill="none"
                stroke="var(--color-green)"
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
              />
            </svg>

            {yTicks.map((tick) => (
              <Text
                key={tick}
                as="span"
                variant="web/body/medium/xs"
                className="text-tertiary absolute right-[calc(100%+12px)] -translate-y-1/2 tabular-nums"
                style={{ top: `${100 - scale(tick, yTicks, false)}%` }}
                aria-hidden="true"
              >
                {tick}
              </Text>
            ))}
            {xTicks.map((tick, index) => (
              <Text
                key={tick}
                as="span"
                variant="web/body/medium/xs"
                className="text-tertiary absolute top-[calc(100%+10px)] -translate-x-1/2 tabular-nums"
                style={{ left: `${scale(tick, xTicks, log)}%` }}
                aria-hidden="true"
              >
                {xTickLabels?.[index] ?? tick}
              </Text>
            ))}

            {[...otherPoints, ...frontierPoints].map((point) => {
              const { left, top } = position(point);
              const highlighted = point.series === frontier;
              return (
                <div
                  key={point.label}
                  className="absolute"
                  style={{ left: `${left}%`, top: `${top}%` }}
                  aria-hidden="true"
                >
                  <span
                    className={`absolute -translate-x-1/2 -translate-y-1/2 rounded-full ${highlighted ? 'size-3 bg-green-50' : 'bg-gray-60 size-2.5'}`}
                  />
                  <Text
                    as="span"
                    variant="web/body/medium/xs"
                    className={`absolute whitespace-nowrap ${labelPositionClassNames[point.labelPosition ?? 'right']} ${highlighted ? 'text-primary' : 'text-secondary'}`}
                  >
                    {point.label}
                  </Text>
                </div>
              );
            })}

            <Text
              as="span"
              variant="web/body/medium/xs"
              className="text-secondary absolute right-0 top-[calc(100%+32px)]"
              aria-hidden="true"
            >
              {xLabel}
            </Text>
          </div>
        </div>
      </div>
      {caption && (
        <figcaption className="article-caption mx-auto mt-3 px-4">
          <Text
            as="span"
            variant="web/body/medium/xs"
            className="text-tertiary"
          >
            {caption}
          </Text>
        </figcaption>
      )}
    </figure>
  );
}
